import { useCallback, useEffect, useRef, useState } from "react"
import {
  DailyVideo,
  useDaily,
  useDailyEvent,
  useDevices,
  useLocalSessionId,
} from "@daily-co/daily-react"
import { AlertCircle, Loader2, Users } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  applyStoredDevicePreferences,
  chooseDevice,
  DEVICE_FALLBACK_NAME_KEYS,
  type DeviceKind,
} from "./devicePreferences"

// What useDevices() returns per device; StatefulDevice isn't exported from
// the package root, so a structural type stands in.
interface DeviceOption {
  device: MediaDeviceInfo
  selected: boolean
}

interface PreJoinScreenProps {
  onJoin: () => void
}

// Daily reports speech peaks around 0.2–0.3, so amplify to fill the meter.
const MIC_LEVEL_SCALE = 300

// The "check your camera and mic" screen (#940): runs the camera before the
// call joins, lets the user pick devices, and remembers the choice. Rendered
// inside the same DailyProvider as the call itself, so whatever is chosen
// here is what the join uses.
export function PreJoinScreen({ onJoin }: PreJoinScreenProps) {
  const { t } = useTranslation()
  const daily = useDaily()
  const localSessionId = useLocalSessionId()
  const {
    cameras,
    microphones,
    speakers,
    currentCam,
    currentMic,
    currentSpeaker,
    camState,
    micState,
    setCamera,
    setMicrophone,
    setSpeaker,
  } = useDevices()
  const [starting, setStarting] = useState(true)
  const [micLevel, setMicLevel] = useState(0)
  const startedRef = useRef(false)
  const joinedRef = useRef(false)

  useEffect(() => {
    if (!daily || startedRef.current) return
    startedRef.current = true
    let cancelled = false
    const start = async () => {
      try {
        await daily.startCamera()
        await applyStoredDevicePreferences(daily)
        // A fast Join (or Back) can unmount this screen while the permission
        // prompt still holds startCamera pending — the meter observer must
        // not start then, or it runs unconsumed for the whole lesson.
        if (!cancelled) await daily.startLocalAudioLevelObserver(200)
      } catch (err) {
        // A denied permission or missing device surfaces through camState.
        console.error("Pre-join device start failed:", err)
      } finally {
        if (!cancelled) setStarting(false)
      }
    }
    void start()
    return () => {
      cancelled = true
    }
  }, [daily])

  useEffect(() => {
    return () => {
      try {
        if (daily?.isLocalAudioLevelObserverRunning()) {
          daily.stopLocalAudioLevelObserver()
        }
      } catch {
        // Releasing the meter on teardown is best-effort.
      }
      // Backing out without joining must release what startCamera acquired:
      // the call object is a module-level singleton that survives React
      // unmount (daily-react deliberately has no destroy-on-unmount), so
      // without this the camera LED stays on while the user browses on.
      // When the unmount IS the join, the call takes the media over.
      if (!joinedRef.current) {
        daily?.destroy().catch(() => {})
      }
    }
  }, [daily])

  useDailyEvent(
    "local-audio-level",
    useCallback((ev: { audioLevel: number }) => setMicLevel(ev.audioLevel), []),
  )

  const deviceSection = (
    kind: DeviceKind,
    label: string,
    list: DeviceOption[],
    current: DeviceOption | undefined,
    apply: (id: string) => Promise<void> | void,
  ) => {
    // Safari lists no audiooutput devices at all, and pre-permission entries
    // can carry empty ids Radix would reject — hide what can't be chosen.
    const options = list.filter((o) => o.device.deviceId !== "")
    if (options.length === 0) return null
    return (
      <div className="space-y-1.5">
        <Label htmlFor={`prejoin-${kind}`}>{label}</Label>
        <Select
          value={current?.device.deviceId ?? ""}
          onValueChange={(id) => chooseDevice(kind, id, options.map((o) => o.device), apply)}
          disabled={starting}
        >
          <SelectTrigger id={`prejoin-${kind}`} aria-label={label}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((o, i) => (
              <SelectItem key={o.device.deviceId} value={o.device.deviceId}>
                {o.device.label || t(DEVICE_FALLBACK_NAME_KEYS[kind], { number: i + 1 })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  const handleJoin = () => {
    joinedRef.current = true
    onJoin()
  }

  const blocked = camState === "blocked" || micState === "blocked"
  // Another app holding the webcam (Zoom, Meet, OBS) or no camera at all:
  // the whole point of this screen is "check your camera", so say why the
  // preview is dark instead of showing an unexplained placeholder.
  const cameraUnavailable =
    !blocked && (camState === "in-use" || camState === "not-found" || camState === "not-supported")

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-6">
        <div className="w-full max-w-3xl grid gap-8 md:grid-cols-[3fr_2fr] md:items-center">
          <div className="space-y-3">
            <div className="aspect-video rounded-lg overflow-hidden bg-muted relative">
              {starting ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="text-xs">{t("liveSession.preJoin.starting")}</span>
                </div>
              ) : localSessionId ? (
                <DailyVideo
                  sessionId={localSessionId}
                  type="video"
                  fit="cover"
                  mirror
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">
                {t("liveSession.preJoin.micLevel")}
              </span>
              <div
                role="meter"
                aria-label={t("liveSession.preJoin.micLevel")}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.min(100, Math.round(micLevel * MIC_LEVEL_SCALE))}
                className="h-2 rounded-full bg-secondary overflow-hidden"
              >
                <div
                  data-testid="mic-level-bar"
                  className="h-full bg-primary rounded-full transition-[width] duration-100"
                  style={{ width: `${Math.min(100, Math.round(micLevel * MIC_LEVEL_SCALE))}%` }}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h1 className="text-lg font-display font-semibold text-foreground">
                {t("liveSession.preJoin.title")}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t("liveSession.preJoin.subtitle")}
              </p>
            </div>

            {blocked && (
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{t("liveSession.errors.devicePermission")}</span>
              </div>
            )}
            {cameraUnavailable && (
              <div className="flex items-start gap-2 text-sm text-destructive">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{t("liveSession.preJoin.cameraUnavailable")}</span>
              </div>
            )}

            {deviceSection("camera", t("liveSession.devices.camera"), cameras, currentCam, setCamera)}
            {deviceSection("microphone", t("liveSession.devices.microphone"), microphones, currentMic, setMicrophone)}
            {deviceSection("speaker", t("liveSession.devices.speakers"), speakers, currentSpeaker, setSpeaker)}

            <Button size="lg" className="w-full" onClick={handleJoin}>
              {t("liveSession.preJoin.join")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
